import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API, STATUS_META, DEADLINE_META } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DocStatusBadge, ServerStatusBadge } from "@/components/DocStatus";
import { AdminNotaryPanel } from "@/components/admin/AdminNotaryPanel";
import { ArrowLeft, Mail, Phone, MapPin, BadgeCheck, Download, Eye, Check, X, Calendar, Search, ChevronLeft, ChevronRight } from "lucide-react";

const WORKLOAD_LABELS = {
  available: "Available in Pool", eligible: "Eligible to Accept", assigned: "Assigned",
  in_progress: "In Progress", attempted: "Attempted", pending_review: "Pending Proof Review",
  completed: "Completed", failed: "Failed / Unable", overdue: "Overdue",
};
const WORKLOAD_TO_STATUS = {
  assigned: "assigned", in_progress: "in_progress", attempted: "not_served",
  pending_review: "pending_review", completed: "completed",
};

export default function AdminServerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});
  const [wlStatus, setWlStatus] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [execAgr, setExecAgr] = useState(null);
  const [storageHealth, setStorageHealth] = useState(null);
  const [ovNote, setOvNote] = useState("");
  const [ovAck, setOvAck] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  const checkStorage = async () => {
    setStorageHealth({ ok: null, detail: "Checking…" });
    try { const { data } = await api.get("/admin/storage/health"); setStorageHealth(data); }
    catch (e) { setStorageHealth({ ok: false, detail: "Health check request failed." }); }
  };

  const loadExecAgr = async () => {
    try { const { data } = await api.get(`/servers/${id}/executed-agreement`); setExecAgr(data); }
    catch (e) { toast.error("Could not load the executed agreement."); }
  };

  const load = async () => {
    try { const { data } = await api.get(`/servers/${id}/detail`); setD(data); }
    catch (e) { toast.error("Failed to load server"); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/admin/agencies").then(({ data }) => setAgencies(data)).catch(() => {}); }, []);

  const setServerAgency = async (val) => {
    try {
      await api.put(`/admin/servers/${id}/agency`, { agency_id: val === "none" ? null : val });
      toast.success("Business updated");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decideDoc = async (key, decision) => {
    setBusy(key + decision);
    try {
      await api.post(`/servers/${id}/documents/${key}/decision`, { decision, notes: notes[key] || "" });
      toast.success(`Document ${decision === "approve" ? "approved" : "rejected"}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const decideServer = async (action, override) => {
    setBusy("server" + action);
    try {
      const payload = (action === "approve" && override)
        ? { notes: "", override: true, override_note: ovNote, override_ack: ovAck }
        : { notes: "" };
      await api.post(`/servers/${id}/${action}`, payload);
      toast.success(action === "approve" ? "Server approved" : "Server rejected");
      setShowOverride(false); setOvNote(""); setOvAck(false);
      await load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? (det.message || "Approval blocked") : formatApiErrorDetail(det));
    }
    finally { setBusy(null); }
  };

  if (!d) return <p className="text-slate-400">Loading…</p>;
  const p = d.profile;
  const perf = d.performance;

  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="server-detail-page">
      <button data-testid="back-to-servers" onClick={() => nav("/admin/servers")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back to process servers
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="server-detail-name">{p.name}</h2>
          <p className="text-sm text-slate-500">{p.email}{p.license_number ? ` · License ${p.license_number}` : ""}</p>
        </div>
        <ServerStatusBadge status={p.server_status} testid="server-detail-status" />
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="approval" data-testid="tab-approval">Approval</TabsTrigger>
          <TabsTrigger value="workload" data-testid="tab-workload">Workload</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
          {["notary", "both"].includes(p.service_capability) && (
            <TabsTrigger value="notary" data-testid="tab-notary">Notary</TabsTrigger>
          )}
        </TabsList>

        {/* NOTARY — signing-agent verification + service counties */}
        {["notary", "both"].includes(p.service_capability) && (
          <TabsContent value="notary">
            <AdminNotaryPanel serverId={id} signingAgent={d.signing_agent} onChanged={load} />
          </TabsContent>
        )}

        {/* PROFILE */}
        <TabsContent value="profile">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="section-profile">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field icon={Mail} label="Email" value={p.email} />
              <Field icon={Phone} label="Phone" value={p.phone || "—"} />
              <Field icon={MapPin} label="Address" value={p.address || "—"} />
              <Field icon={BadgeCheck} label="License / approval" value={p.license_number || "—"} />
              <Field icon={BadgeCheck} label="Classification" value={{ process_server: "Process Server", notary: "Notary", both: "Process Server & Notary" }[p.service_capability] || "—"} />
              <Field icon={Calendar} label="Registered" value={p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"} />
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Account status</p>
                <div className="mt-1"><ServerStatusBadge status={p.server_status} /></div>
              </div>
            </div>
            {p.background_info && <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{p.background_info}</p>}
          </div>
          {(d.provider_agreement || d.ic_agreement) && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="section-executed-agreements">
              <h3 className="font-display text-base font-bold text-slate-900">Executed agreements</h3>
              <div className="mt-3">
                <Button size="sm" variant="outline" data-testid="view-exec-subscriber-agreement" onClick={loadExecAgr}>
                  View executed Subscriber Agreement (Exhibit E)
                </Button>
              </div>
              {execAgr && (
                <div data-testid="exec-agreement-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setExecAgr(null)}>
                  <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between border-b border-slate-200 p-4">
                      <h3 className="font-display text-lg font-bold text-slate-900">{execAgr.title} <span className="text-xs font-normal text-slate-400">Version {execAgr.version}</span></h3>
                      <button data-testid="close-exec-agreement" onClick={() => setExecAgr(null)} className="text-slate-400 hover:text-slate-700">✕</button>
                    </div>
                    <pre data-testid="exec-agreement-content" className="flex-1 overflow-auto whitespace-pre-wrap p-4 font-sans text-xs leading-relaxed text-slate-700">{execAgr.content}</pre>
                  </div>
                </div>
              )}
              {d.provider_agreement && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3" data-testid="exec-provider-agreement">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{d.provider_agreement.title} <span className="text-xs font-normal text-slate-400">v{d.provider_agreement.version}</span></p>
                    {d.provider_agreement.accepted
                      ? <p className="text-xs text-slate-400">Accepted by {d.provider_agreement.signer || p.name} · {d.provider_agreement.accepted_at ? new Date(d.provider_agreement.accepted_at).toLocaleString() : ""}{d.provider_agreement.ip_address ? ` · IP ${d.provider_agreement.ip_address}` : ""}</p>
                      : <p className="text-xs text-slate-500">{d.provider_agreement.status_label}</p>}
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${d.provider_agreement.status === "new_v1_accepted" ? "bg-emerald-50 text-emerald-700" : d.provider_agreement.status === "legacy_grandfathered" ? "bg-slate-100 text-slate-600" : d.provider_agreement.status === "not_applicable" ? "bg-slate-50 text-slate-400" : "bg-amber-50 text-amber-700"}`}>{d.provider_agreement.status_label}</span>
                </div>
              )}
              {d.ic_agreement && d.ic_agreement.accepted && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3" data-testid="exec-ic-agreement">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{d.ic_agreement.title} <span className="text-xs font-normal text-slate-400">v{d.ic_agreement.version} (legacy)</span></p>
                    <p className="text-xs text-slate-400">Signed by {d.ic_agreement.signer || p.name} · {d.ic_agreement.accepted_at ? new Date(d.ic_agreement.accepted_at).toLocaleString() : ""}</p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">History</span>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents">
          {d.provider_agreement && d.provider_agreement.mode === "v1" && (
            <div className="mb-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="section-provider-agreement">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold text-slate-900">{d.provider_agreement.title}</h3>
                  <p className="text-xs text-slate-500">
                    Version {d.provider_agreement.version}
                    {d.provider_agreement.accepted_at ? ` · Accepted ${new Date(d.provider_agreement.accepted_at).toLocaleString()}` : ""}
                    {d.provider_agreement.effective_date ? ` · Effective ${d.provider_agreement.effective_date}` : ""}
                    {d.provider_agreement.signer ? ` · Signed “${d.provider_agreement.signer}”` : ""}
                  </p>
                  {d.provider_agreement.document_hash && (
                    <p className="mt-1 break-all font-mono text-[10px] text-slate-400" data-testid="provider-agreement-hash">SHA-256: {d.provider_agreement.document_hash}</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span data-testid="provider-agreement-status-label" className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${d.provider_agreement.status === "new_v1_accepted" ? "bg-emerald-50 text-emerald-700" : d.provider_agreement.status === "legacy_grandfathered" ? "bg-slate-100 text-slate-600" : d.provider_agreement.status === "not_applicable" ? "bg-slate-50 text-slate-400" : "bg-amber-50 text-amber-700"}`}>{d.provider_agreement.status_label}</span>
                  {d.provider_agreement.accepted && (
                    <a data-testid="provider-agreement-view" href={`${API}/servers/${id}/provider-agreement/document`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
                      <Eye className="h-3.5 w-3.5" /> View executed
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-white" data-testid="section-documents">
            <div className="border-b border-slate-100 px-6 py-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">Required documents</h3>
                  <p className="text-sm text-slate-500">
                    {d.onboarding.summary.approved} approved · {d.onboarding.summary.pending} pending · {d.onboarding.summary.rejected} rejected · {d.onboarding.summary.missing} missing
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Button size="sm" variant="outline" onClick={checkStorage} data-testid="check-storage-health-button" className="h-8">
                    Check storage
                  </Button>
                  {storageHealth && (
                    <span data-testid="storage-health-status"
                      className={`text-xs font-medium ${storageHealth.ok === null ? "text-slate-500" : storageHealth.ok ? "text-emerald-600" : "text-red-600"}`}>
                      {storageHealth.ok === null ? "Checking…" : storageHealth.ok ? "Storage OK" : `Storage failed — ${storageHealth.detail}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {d.onboarding.documents.map((doc) => (
                <div key={doc.key} data-testid={`admin-doc-row-${doc.key}`} className="px-6 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{doc.label}</p>
                      {doc.submitted_at && <p className="text-xs text-slate-400">Submitted {new Date(doc.submitted_at).toLocaleString()}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <DocStatusBadge status={doc.status} testid={`admin-doc-status-${doc.key}`} />
                      {doc.file_id && (
                        <>
                          <a data-testid={`admin-doc-view-${doc.key}`} href={`${API}/server/documents/${doc.key}/download?server_id=${id}&disposition=inline`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
                            <Eye className="h-3.5 w-3.5" /> View
                          </a>
                          <a data-testid={`admin-doc-download-${doc.key}`} href={`${API}/server/documents/${doc.key}/download?server_id=${id}&disposition=attachment`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:underline">
                            <Download className="h-3.5 w-3.5" /> Download
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  {["pending_approval", "submitted", "rejected"].includes(doc.status) && doc.file_id && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Input data-testid={`admin-doc-notes-${doc.key}`} placeholder="Optional note" className="h-9 max-w-xs"
                        value={notes[doc.key] || ""} onChange={(e) => setNotes({ ...notes, [doc.key]: e.target.value })} />
                      <Button size="sm" disabled={busy} onClick={() => decideDoc(doc.key, "approve")} data-testid={`admin-doc-approve-${doc.key}`} className="bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => decideDoc(doc.key, "reject")} data-testid={`admin-doc-reject-${doc.key}`} className="border-red-300 text-red-600 hover:bg-red-50"><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* APPROVAL */}
        <TabsContent value="approval">
          <div className="space-y-4" data-testid="section-approval">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-bold text-slate-900">Overall account approval</h3>
                  <p className="text-sm text-slate-500">A provider cannot be approved until every required document is approved.</p>
                </div>
                <div className="flex items-center gap-2">
                  <ServerStatusBadge status={p.server_status} />
                  {d.onboarding.approval_type === "exception" && (
                    <span data-testid="approved-with-exception-badge" className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">Approved with exception</span>
                  )}
                </div>
              </div>
              {(() => {
                const req = (d.onboarding.documents || []).filter((x) => !x.optional);
                const blocking = req.filter((x) => x.status !== "approved");
                const allApproved = blocking.length === 0;
                return (
                  <>
                    {!allApproved && (
                      <div data-testid="approval-blocking-list" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        <p className="font-semibold">Blocking documents ({blocking.length}):</p>
                        <ul className="mt-1 list-disc pl-5">
                          {blocking.map((b) => <li key={b.key} data-testid={`approval-blocking-${b.key}`}>{b.label} — <strong>{b.status}</strong></li>)}
                        </ul>
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button size="sm" disabled={busy || !allApproved} onClick={() => decideServer("approve")} data-testid="approve-server-account" className="bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> Approve account</Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => decideServer("reject")} data-testid="reject-server-account" className="border-red-300 text-red-600 hover:bg-red-50"><X className="mr-1 h-3.5 w-3.5" /> Reject account</Button>
                      {!allApproved && p.server_status !== "active" && (
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setShowOverride((v) => !v)} data-testid="toggle-override" className="border-amber-400 text-amber-700 hover:bg-amber-50">Approve with exception…</Button>
                      )}
                    </div>
                    {showOverride && !allApproved && (
                      <div data-testid="override-panel" className="mt-4 space-y-3 rounded-lg border border-amber-300 bg-white p-4">
                        <p className="text-sm font-semibold text-amber-800">Admin exception — required documents are not all approved</p>
                        <textarea data-testid="override-note" value={ovNote} onChange={(e) => setOvNote(e.target.value)} rows={3}
                          placeholder="Explain why this approval is being made despite missing documents (minimum 30 characters)…"
                          className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                        <p className="text-xs text-slate-500">{ovNote.trim().length}/30 characters</p>
                        <label className="flex items-start gap-2 text-sm text-slate-700">
                          <input type="checkbox" data-testid="override-ack" checked={ovAck} onChange={(e) => setOvAck(e.target.checked)} className="mt-0.5" />
                          I acknowledge this is an exception approval and it will be permanently recorded with my name, the time, and a snapshot of the missing documents.
                        </label>
                        <Button size="sm" disabled={busy || ovNote.trim().length < 30 || !ovAck} onClick={() => decideServer("approve", true)} data-testid="confirm-override" className="bg-amber-600 text-white hover:bg-amber-700">Record exception & approve</Button>
                      </div>
                    )}
                    {d.onboarding.approval_type === "exception" && d.onboarding.approval_override && (
                      <div data-testid="override-record" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <p className="font-semibold">Exception approval on record</p>
                        <p className="mt-1">By {d.onboarding.approval_override.admin_name} · {d.onboarding.approval_override.at ? new Date(d.onboarding.approval_override.at).toLocaleString() : ""}</p>
                        <p className="mt-1 whitespace-pre-wrap"><strong>Note:</strong> {d.onboarding.approval_override.note}</p>
                        {(d.onboarding.approval_override.missing_snapshot || []).length > 0 && (
                          <div className="mt-2">
                            <p className="font-semibold">Missing at approval:</p>
                            <ul className="list-disc pl-5">
                              {d.onboarding.approval_override.missing_snapshot.map((m) => <li key={m.key}>{m.label} — {m.status}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="server-agency-card">
              <h3 className="font-display text-lg font-bold text-slate-900">White-label business</h3>
              <p className="text-sm text-slate-500">Link this server to a participating process-serving business (agency). Their portal and new orders carry this business identity. Authorization is unaffected.</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Select value={p.agency_id || "none"} onValueChange={setServerAgency}>
                  <SelectTrigger data-testid="server-agency-select" className="max-w-xs"><SelectValue placeholder="No business" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No business (ARPS direct)</SelectItem>
                    {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {p.agency_name && <span data-testid="server-agency-current" className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">{p.agency_name}</span>}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="font-display text-lg font-bold text-slate-900">Approval history</h3>
              <div className="mt-3 space-y-2" data-testid="approval-history">
                {(d.onboarding.onboarding_log || []).length === 0 && <p className="text-sm text-slate-400">No approval activity yet.</p>}
                {(d.onboarding.onboarding_log || []).slice().reverse().map((e, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 border-b border-slate-50 py-2 text-sm last:border-0">
                    <div>
                      <span className="font-medium text-slate-900">{e.detail || e.event}</span>
                      {e.notes && <p className="text-xs text-slate-500">Note: {e.notes}</p>}
                    </div>
                    <span className="whitespace-nowrap text-xs text-slate-400">{e.by} · {e.at ? new Date(e.at).toLocaleString() : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* WORKLOAD */}
        <TabsContent value="workload">
          {d.eligibility && !d.eligibility.eligible && (
            <div data-testid="workload-eligibility-banner" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              This server is <strong>not eligible</strong> for assignments yet — {d.eligibility.docs_approved}/{d.eligibility.docs_required} documents approved
              {!d.eligibility.account_complete && ", account incomplete"}
              {!d.eligibility.admin_approved && ", awaiting admin account approval"}. Eligible jobs: 0.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="section-workload">
            {Object.keys(WORKLOAD_LABELS).map((k) => (
              <button key={k} data-testid={`workload-${k}`}
                onClick={() => { if (k === "available" || k === "eligible") { nav("/admin/jobs"); return; } setWlStatus(WORKLOAD_TO_STATUS[k] || null); document.getElementById("jobs-anchor")?.scrollIntoView({ behavior: "smooth" }); }}
                className={`rounded-xl border p-4 text-left transition hover:shadow-md ${wlStatus && wlStatus === WORKLOAD_TO_STATUS[k] ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                <p className="font-display text-2xl font-bold text-slate-900">{d.workload[k]}</p>
                <p className="mt-0.5 text-xs text-slate-500">{WORKLOAD_LABELS[k]}</p>
              </button>
            ))}
          </div>
          <div id="jobs-anchor" className="mt-4"><ServerJobs key={wlStatus || "all"} id={id} nav={nav} initialStatus={wlStatus || "all"} /></div>
        </TabsContent>

        {/* PERFORMANCE */}
        <TabsContent value="performance">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="section-performance">
            <Metric label="Total jobs" value={perf.total_jobs} />
            <Metric label="Active jobs" value={perf.active_jobs} />
            <Metric label="Completed jobs" value={perf.completed_jobs} />
            <Metric label="Attempts" value={perf.attempts} />
            <Metric label="Successful serves" value={perf.successful_serves} />
            <Metric label="Serve rate" value={`${perf.serve_rate}%`} highlight />
            <Metric label="Avg attempts / job" value={perf.avg_attempts_per_job} />
            <Metric label="Pending jobs" value={perf.pending_jobs} />
            <Metric label="Overdue jobs" value={perf.overdue_jobs} danger={perf.overdue_jobs > 0} />
          </div>
        </TabsContent>

        {/* JOBS */}
        <TabsContent value="jobs">
          <ServerJobs id={id} nav={nav} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 flex items-center gap-2 text-sm font-medium text-slate-900"><Icon className="h-4 w-4 text-slate-400" /> {value}</p>
    </div>
  );
}

function Metric({ label, value, highlight, danger }) {
  return (
    <div className={`rounded-xl border p-4 ${danger ? "border-red-200 bg-red-50" : highlight ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`}>
      <p className={`font-display text-2xl font-bold ${danger ? "text-red-700" : highlight ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
      <p className="mt-0.5 text-xs text-slate-500">{label}</p>
    </div>
  );
}

const SERVER_JOB_STATUS = [
  { v: "all", l: "All statuses" },
  { v: "assigned", l: "Assigned" },
  { v: "in_progress", l: "In Progress" },
  { v: "not_served", l: "Attempted / Not served" },
  { v: "pending_review", l: "Pending Review" },
  { v: "completed", l: "Completed" },
];
const REASSIGN_OPTS = [
  { v: "all", l: "Worked jobs (all)" },
  { v: "reassigned", l: "Worked · was reassigned" },
  { v: "never", l: "Worked · never reassigned" },
  { v: "reassigned_away", l: "Reassigned away (history)" },
];

function ServerJobs({ id, nav, initialStatus = "all" }) {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState(initialStatus);
  const [svc, setSvc] = useState("all");
  const [reassignment, setReassignment] = useState("all");
  const [sort, setSort] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, service_types: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/servers/${id}/jobs`, { params: { q: term, status, service_type: svc, reassignment, sort, date_from: dateFrom, date_to: dateTo, page, page_size: 50 } })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData({ items: [], total: 0, page: 1, pages: 1, service_types: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, term, status, svc, reassignment, sort, dateFrom, dateTo, page]);

  const reset = (fn) => (v) => { setPage(1); fn(v); };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="server-jobs-table">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
        <form className="relative min-w-48 flex-1" onSubmit={(e) => { e.preventDefault(); setPage(1); setTerm(q); }}>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="server-jobs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job #, defendant, client, case #, address… (Enter)" className="h-9 pl-9" />
        </form>
        <Select value={status} onValueChange={reset(setStatus)}>
          <SelectTrigger data-testid="server-jobs-status" className="h-9 w-44"><SelectValue /></SelectTrigger>
          <SelectContent>{SERVER_JOB_STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={reassignment} onValueChange={reset(setReassignment)}>
          <SelectTrigger data-testid="server-jobs-reassignment" className="h-9 w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{REASSIGN_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={svc} onValueChange={reset(setSvc)}>
          <SelectTrigger data-testid="server-jobs-service-type" className="h-9 w-40"><SelectValue placeholder="Service type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All service types</SelectItem>
            {(data.service_types || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="server-jobs-date-from" type="date" value={dateFrom} onChange={(e) => { setPage(1); setDateFrom(e.target.value); }} className="h-9 w-36" />
        <Input data-testid="server-jobs-date-to" type="date" value={dateTo} onChange={(e) => { setPage(1); setDateTo(e.target.value); }} className="h-9 w-36" />
        <Button data-testid="server-jobs-sort" variant="outline" size="sm" onClick={() => { setPage(1); setSort(sort === "desc" ? "asc" : "desc"); }}>
          {sort === "desc" ? "Newest → Oldest" : "Oldest → Newest"}
        </Button>
        <Button data-testid="server-jobs-clear" variant="ghost" size="sm" onClick={() => { setQ(""); setTerm(""); setStatus("all"); setSvc("all"); setReassignment("all"); setSort("desc"); setDateFrom(""); setDateTo(""); setPage(1); }}>Clear all</Button>
      </div>
      <div className="px-5 py-2 text-xs text-slate-500" data-testid="server-jobs-count">
        <span className="font-semibold text-slate-900">{(data.total || 0).toLocaleString()}</span> job(s) · sorted Newest → Oldest by default
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Job #</th><th className="px-4 py-2.5">Defendant</th>
              <th className="px-4 py-2.5">Client</th><th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Deadline</th><th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Server (orig → current)</th><th className="px-4 py-2.5">Attempts</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No jobs match.</td></tr>}
            {!loading && data.items.map((j) => (
              <tr key={j.id} data-testid={`server-job-${j.job_number}`} className="cursor-pointer border-t border-slate-50 hover:bg-slate-50" onClick={() => nav(`/admin/jobs/${j.id}`)}>
                <td className="px-4 py-2.5 font-mono-arps font-semibold text-slate-900">#{j.job_number}</td>
                <td className="px-4 py-2.5 text-slate-700">{j.defendant}</td>
                <td className="px-4 py-2.5 text-slate-600">{j.client_name}</td>
                <td className="px-4 py-2.5 text-slate-600">{j.service_type}</td>
                <td className="px-4 py-2.5 text-slate-600">{j.deadline || "—"}</td>
                <td className="px-4 py-2.5">{STATUS_META[j.status]?.label || j.status}{j.overdue && <span className="ml-1 text-[10px] font-bold text-red-600">OVERDUE</span>}</td>
                <td className="px-4 py-2.5 text-slate-600">
                  {j.reassigned
                    ? <span title={`Reassigned ${j.reassigned_at ? new Date(j.reassigned_at).toLocaleString() : ""} by ${j.reassigned_by || "—"}${j.reassign_reason ? " · " + j.reassign_reason : ""}`}>{j.original_server_name || "—"} <span className="text-slate-400">→</span> {j.current_server_name || "—"} <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`reassigned-badge-${j.job_number}`}>Reassigned</span></span>
                    : (j.current_server_name || "—")}
                </td>
                <td className="px-4 py-2.5 text-slate-700">{j.attempts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-3 border-t border-slate-100 py-3">
          <Button data-testid="server-jobs-prev" variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}><ChevronLeft className="mr-1 h-4 w-4" /> Prev</Button>
          <span className="text-sm text-slate-500">Page {data.page} / {data.pages}</span>
          <Button data-testid="server-jobs-next" variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)}>Next <ChevronRight className="ml-1 h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
